/* eslint-disable no-empty */
import {
  RequestBaseGetTaskPossibleAssignees,
  RequestBasePostClubTask,
  RequestBasePostMultiTaskAssignments,
  RequestBasePostTaskAssignments,
  RequestBaseUpdateTask,
  RequestBasketballPostTeamTask,
  ResponseBaseGetClubTasks,
  ResponseBaseGetTaskEvents,
  ResponseBaseGetTaskPossibleAssignees,
  ResponseBaseGetUserTaskAssignments,
  ResponseBasePostTaskAssignments,
  ResponseBasketballGetTeamTasks,
  ResponseBasketballPostTeamTask,
  SelectOption,
  Task,
  TaskEventType,
} from '@hefe/models';
import { FetchBaseQueryMeta, createApi } from '@reduxjs/toolkit/query/react';
import API from '../../../api/api';

export const tasksApi = createApi({
  reducerPath: 'apiTasks',
  baseQuery: API.baseQuery,
  endpoints: (builder) => ({
    fetchClubTasks: builder.query<ResponseBaseGetClubTasks, void>({
      query: () => ({ url: '/getClubTasks' }),
      transformResponse: (response: any) => response?.data,
    }),
    createClubTask: builder.mutation<any, RequestBasePostClubTask>({
      query: (data) => ({
        url: '/createClubTasks',
        method: 'POST',
        body: data,
      }),
      transformResponse: (response: any) => response?.data,
    }),
    fetchBasketballTeamTasks: builder.query<
      ResponseBasketballGetTeamTasks,
      { teamId: number; withClubTasks: boolean }
    >({
      query: ({ teamId, withClubTasks }) => ({
        url: '/getBasketballTeamTasks',
        params: { teamId, withClubTasks },
      }),
      transformResponse: (response: any) => response?.data,
    }),
    createBasketballTeamTask: builder.mutation<
      ResponseBasketballPostTeamTask,
      { teamId: number; request: RequestBasketballPostTeamTask }
    >({
      query: ({ teamId, request }) => ({
        url: `/createBasketballTeamTask/${teamId}`,
        method: 'POST',
        body: request,
      }),
      transformResponse: (response: any) => response?.data,
    }),
    updateTask: builder.mutation<
      RequestBaseUpdateTask,
      { taskId: number; request: RequestBaseUpdateTask }
    >({
      query: ({ taskId, request }) => ({
        url: `/update/${taskId}`,
        method: 'PUT',
        body: request,
      }),
      transformResponse: (response: any) => response?.data,
    }),
    deleteTask: builder.mutation<Task, Task>({
      query: (task) => ({
        url: `/delete/${task.id}`,
        method: 'DELETE',
        meta: task, // pass task object as meta argument
      }),
      transformResponse: (response: any, meta: FetchBaseQueryMeta) =>
        meta as unknown as Task,
    }),
    fetchTaskEvents: builder.query<
      ResponseBaseGetTaskEvents,
      { taskId: number; teamId?: number }
    >({
      query: ({ taskId, teamId }) => ({
        url: `/tasks/${taskId}/events`,
        params: { 'basketball-team-id': teamId },
      }),
      transformResponse: (response: ResponseBaseGetTaskEvents) => {
        return response;
      },
    }),
    /**
     * Assign users/teams to task
     */
    assignToTask: builder.mutation<
      ResponseBasePostTaskAssignments,
      {
        taskId: number;
        request: RequestBasePostTaskAssignments;
      }
    >({
      query: ({ taskId, request }) => ({
        url: `/tasks/${taskId}/assignments`,
        method: 'POST',
        body: request,
      }),
      transformResponse: (response: ResponseBasePostTaskAssignments) =>
        response,
      async onQueryStarted({ taskId, request }, { dispatch, queryFulfilled }) {
        try {
          const { data: responseData } = await queryFulfilled;
          dispatch(
            tasksApi.util.updateQueryData(
              'fetchTaskEvents',
              { taskId: taskId, teamId: request.basketball_team_id },
              (draft) => {
                if (!draft.events) return;
                for (const event of draft.events) {
                  let eventId = '0_' + request.game_id;
                  let taskType = TaskEventType.BASKETBALL_GAME;
                  if (request.practice_id) {
                    taskType = TaskEventType.BASKETBALL_PRACTICE;
                    eventId = '1_' + request.practice_id;
                  }
                  if (event.id === eventId && event.type === taskType) {
                    event.num_of_user_assignments =
                      responseData.number_of_all_user_assignments;
                    event.assignments = responseData.assignments;
                  }
                }
              }
            )
          );
        } catch (error: unknown) {}
      },
    }),
    multiAssignToTask: builder.mutation<
      ResponseBaseGetTaskEvents,
      {
        taskId: number;
        request: RequestBasePostMultiTaskAssignments;
      }
    >({
      query: ({ taskId, request }) => ({
        url: `/tasks/${taskId}/assignments/multi`,
        method: 'POST',
        body: request,
      }),
      transformResponse: (response: ResponseBaseGetTaskEvents) => response,
      async onQueryStarted({ taskId, request }, { dispatch, queryFulfilled }) {
        try {
          const { data: responseData } = await queryFulfilled;
          const fetchTaskEventsArgs = {
            taskId: taskId,
          } as any;
          if (request.basketball_team_id) {
            fetchTaskEventsArgs['teamId'] =
              request.basketball_team_id ?? undefined;
          }

          dispatch(
            tasksApi.util.updateQueryData(
              'fetchTaskEvents',
              fetchTaskEventsArgs,
              (draft) => {
                if (!draft.events) return;

                draft.events = responseData.events;
                draft.task = responseData.task;
              }
            )
          );
        } catch (error: unknown) {}
      },
    }),

    fetchPossibleTaskAssignees: builder.query<
      ResponseBaseGetTaskPossibleAssignees,
      {
        taskId: number;
        request: RequestBaseGetTaskPossibleAssignees;
      }
    >({
      query: ({ taskId, request }) => ({
        url: `/tasks/${taskId}/possible-assignees`,
        method: 'GET',
        params: {
          'game-id': request.game_id,
          'practice-id': request.practice_id,
          'only-users': request.only_users ? 1 : 0,
          'only-basketball-teams': request.only_basketball_teams ? 1 : 0,
        },
      }),
    }),
    fetchUserTaskAssignments: builder.query<
      ResponseBaseGetUserTaskAssignments | SelectOption[],
      { userId?: number; select?: boolean }
    >({
      query: ({ userId, select }) => ({
        url: '/user/tasks/assignments',
        params: { userId, select: select ? 1 : 0 },
      }),
    }),
  }),
});

export const userTaskAssignmentsSelector =
  (teamId?: number) => (state: any) => {
    const query =
      state.apiTasks?.queries[
        'fetchUserTaskAssignments({"userId":' + teamId + '})'
      ];

    if (!query?.data) {
      return [];
    }

    return query.data.map(
      (assignment: any) =>
        ({
          label: assignment.task.name,
          value: assignment.id,
        } as SelectOption)
    );
  };

// Export hooks for usage in functional components
export const {
  useFetchClubTasksQuery,
  useCreateClubTaskMutation,
  useFetchBasketballTeamTasksQuery,
  useCreateBasketballTeamTaskMutation,
  useUpdateTaskMutation,
  useDeleteTaskMutation,
  useFetchTaskEventsQuery,
  useLazyFetchTaskEventsQuery,
  useAssignToTaskMutation,
  useMultiAssignToTaskMutation,
  useFetchUserTaskAssignmentsQuery,
  useLazyFetchPossibleTaskAssigneesQuery,
  reducerPath,
  reducer,
  middleware,
} = tasksApi;
